import { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { usePathname } from '@/lib/navigation';
import { rememberLocation } from '@/lib/crumb-return';
import { useSession } from '@/contexts/SessionContext';
import { switchPersona } from '@/lib/auth';
import {
    clearCrossPersonaCaches,
    resolvePersonaFallback,
    intendedPersonaForFrontend,
} from '@/lib/persona-switch';
import { FRONTENDS, isRouteAuthorised, sectionForPathname } from '@/lib/frontends';
import { grantsAdministration, isActivePersona, type StaffShellKey } from '@septima/contract';
import { useToast } from '@/lib/useToast';
import { useSidebarCollapse } from '@/lib/use-sidebar-collapse';
import NotAuthorisedPage from '@/pages/not-authorised/page';
import { PageToolbarProvider } from '@/components/ui/PageToolbar';
import AppSidebar from './AppSidebar';
import MobileTopBar from './MobileTopBar';
import StaffPageToolbar from './StaffPageToolbar';
import WipBanner from './WipBanner';

interface FrontendLayoutProps {
    frontend: StaffShellKey;
    children: ReactNode;
}

export default function FrontendLayout({ frontend, children }: FrontendLayoutProps) {
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    // The icon rail of the desktop sidebar: a cookie holds the choice, and
    // Cmd/Ctrl+\ or `[` toggles it.
    const sidebar = useSidebarCollapse();
    const pathname = usePathname();
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { pushToast } = useToast();
    const { user, grantedFrontends, refresh, setActivePersona, isHydrating } = useSession();
    const activePersona = user?.activePersona ?? null;
    // The Admin shell is not a persona (ADR-0004). It opens on the granted
    // `admin` token, and it keeps the active persona of the session as it is.
    const isAdminShell = frontend === 'admin';
    // `grantedFrontends` is the allowlist filtered to the four staff views
    // (never 'admin'), so recovery can only ever land on a real view route.
    const isGranted = isAdminShell
        ? grantsAdministration(user?.personas ?? [])
        : grantedFrontends.includes(frontend);
    // Route authorisation (ADR-0004 / issue #209). The persona this shell runs
    // as for this user (`im-assistant → desk` is the only non-identity case);
    // the section guard below is checked against it + the granted tokens, so
    // the verdict is timing-independent of the optimistic activePersona switch.
    // The Admin shell runs as the active persona of the session.
    const shellPersona =
        frontend === 'admin'
            ? activePersona && isActivePersona(activePersona)
                ? activePersona
                : 'view'
            : user
              ? intendedPersonaForFrontend(frontend, user.personas)
              : frontend;
    // Authorised when (a) the frontend itself is granted (cross-frontend bar)
    // AND (b) the active persona may see the section the pathname falls under
    // (im-assistant peer-review bar; admin-only Administration).
    // Derived from the SAME policy as the sidebar, so visibility and access
    // cannot drift. An unauthorised route renders the 403 page IN PLACE (the
    // URL is kept; no silent redirect) instead of staff chrome.
    const routeAuthorised =
        isGranted && isRouteAuthorised(pathname, shellPersona, user?.personas ?? []);
    // An unfinished page (`wip` on the section the URL falls under) takes a
    // "not complete yet" strip above its own header. The same flag drives the
    // sidebar "WIP" badge and the trailing sidebar group, so the marker and the
    // notice cannot drift. A finished page renders exactly as before: the
    // wrapper below is present only when the banner is.
    const isWip = sectionForPathname(frontend, pathname)?.wip === true;
    // Track the most-recent (frontend, activePersona) we've already
    // requested a switch for, so React's dev-mode double-invoke and a
    // mid-flight `refresh` re-render don't fire duplicate /api/auth/persona
    // calls (and the corresponding duplicate audit rows).
    const lastSwitchRef = useRef<string | null>(null);
    // Frontends the server has denied a switch to this session — never retry
    // them; bounce to a valid persona instead.
    const deniedRef = useRef<Set<string>>(new Set());

    // Remember the query string of each page, so that a breadcrumb link back to
    // a list returns to the sort, search and filters that the user left.
    useEffect(() => {
        rememberLocation(location.pathname, location.search);
    }, [location.pathname, location.search]);

    // Auto-close mobile nav when route changes.
    useEffect(() => {
        setMobileNavOpen(false);
    }, [pathname]);

    // Deep-link auth gate (ADR-0004). The `/` index gates auth, but deep-linking
    // straight to e.g. `/desk/portfolio` while unauthenticated would otherwise
    // render staff chrome. Once the session has hydrated and there is no user,
    // bounce to /login. While hydrating or unauthenticated (pre-redirect) we
    // render a minimal loading state below, so unauthenticated chrome never
    // flashes. Ordered BEFORE the persona-switch effect — there is no persona to
    // reconcile until the user is known.
    useEffect(() => {
        if (isHydrating) return;
        if (!user) {
            // Keep the target, so the user returns to it after sign-in.
            // `LoginPage` runs the value through `safeRedirect`.
            const target = `${location.pathname}${location.search}`;
            navigate(`/login?callbackUrl=${encodeURIComponent(target)}`, { replace: true });
        }
    }, [isHydrating, user, navigate, location.pathname, location.search]);

    // Keep the session's activePersona in lock-step with the rendered view
    // (ADR-0004), optimistically (PRD #156). On entering a granted frontend
    // we reflect the switch in the UI immediately, then fire the server-side
    // switch + audit write in the background — the route is usable without
    // waiting on it. A denied switch (lost grant / audit failure) or a
    // deep-link into an ungranted view recovers to a valid persona via the
    // existing toast/error pattern, so the shell never sits on a persona the
    // server will gate every read against.
    useEffect(() => {
        if (!user) return;
        // The Admin shell switches no persona: the server admits its endpoints
        // on the granted `admin` token, whatever the active persona is.
        if (frontend === 'admin') return;
        // A deep-link / saved URL into an ungranted frontend renders the 403 page
        // IN PLACE (issue #209) — no silent redirect. We do NOT switch persona or
        // navigate here; the shell never runs as a persona it has no grant for,
        // and the `routeAuthorised` gate below renders NotAuthorisedPage.
        if (!isGranted) return;
        // The active persona this shell should run as for this user — on the Desk
        // shell an `im-assistant`-only user runs as `im-assistant`, NOT the
        // ungranted `desk` (ADR-0004).
        const intendedPersona = intendedPersonaForFrontend(frontend, user.personas);
        if (activePersona === intendedPersona) return;

        if (deniedRef.current.has(frontend)) {
            const fallback = resolvePersonaFallback(activePersona, grantedFrontends, frontend);
            if (fallback && fallback !== frontend) {
                setActivePersona(fallback);
                navigate(`/${fallback}`, { replace: true });
            }
            return;
        }

        const key = `${intendedPersona}:${activePersona ?? 'unset'}`;
        if (lastSwitchRef.current === key) return;
        lastSwitchRef.current = key;

        // Optimistic: reflect the new persona now; reconcile the session after.
        const previousPersona = activePersona;
        setActivePersona(intendedPersona);
        // Cross-persona cache wall (ADR-0004): domain query keys are
        // persona-blind and this switch is an SPA navigation, so a payload
        // cached under the previous persona (e.g. a comply Suitability Record
        // detail carrying the KYC assessmentContent block — ADR-0015 D5)
        // would otherwise render from cache after switching. Fires only on a
        // REAL persona change — this branch is guarded by the
        // activePersona !== intendedPersona check and the lastSwitchRef
        // dedupe above. The session entry is kept (no hydration flash).
        clearCrossPersonaCaches(queryClient);

        void switchPersona(intendedPersona).then((res) => {
            if (res.ok) {
                // Reconcile session state in the background; route already rendered.
                void refresh();
                return;
            }
            // Denied / errored — recover to a persona the server will honour.
            deniedRef.current.add(frontend);
            lastSwitchRef.current = null;
            pushToast({
                kind: 'error',
                message: 'Could not switch view — returning to your previous view.',
            });
            const fallback = resolvePersonaFallback(previousPersona, grantedFrontends, frontend);
            if (fallback && fallback !== frontend) {
                setActivePersona(fallback);
                navigate(`/${fallback}`, { replace: true });
            }
        });
    }, [
        user,
        frontend,
        activePersona,
        isGranted,
        grantedFrontends,
        refresh,
        setActivePersona,
        pushToast,
        navigate,
        queryClient,
    ]);

    // While the session hydrates, or after it resolves to no user (the effect
    // above is mid-redirect to /login), render the loading state rather than the
    // staff shell — matches the `/` index gate's markup (home.tsx).
    if (isHydrating || !user) {
        return (
            <div className="flex-1 flex items-center justify-center text-body text-[rgb(var(--color-text-secondary))]">
                Loading…
            </div>
        );
    }

    // Route authorisation (issue #209). A saved / typed URL the active persona
    // is not authorised for renders the existing 403 page IN PLACE — the URL is
    // kept (no redirect) so the user sees WHY rather than a blank/broken page.
    // The backend is the real boundary (its endpoints 403); this is the UX
    // mirror, derived from the same persona policy as the sidebar.
    if (!routeAuthorised) {
        return <NotAuthorisedPage variant="route" />;
    }

    return (
        <div className="flex flex-col lg:flex-row h-full overflow-hidden">
            <MobileTopBar
                onOpen={() => setMobileNavOpen(true)}
                frontendLabel={FRONTENDS[frontend].shortLabel}
            />

            {mobileNavOpen && (
                <button
                    type="button"
                    aria-label="Close navigation menu"
                    onClick={() => setMobileNavOpen(false)}
                    className="lg:hidden fixed inset-0 z-40 bg-[var(--septima-scrim)]"
                />
            )}

            <AppSidebar
                frontend={frontend}
                mobileOpen={mobileNavOpen}
                onMobileClose={() => setMobileNavOpen(false)}
                collapsed={sidebar.collapsed}
                onToggleCollapsed={sidebar.toggle}
                shortcutLabel={sidebar.shortcutLabel}
            />

            {/* The layout owns the toolbar strip, so each page gets the
                breadcrumb and the global buttons. A page gives its title and
                controls to the strip with `<PageHeader>` (see PageToolbar). */}
            <PageToolbarProvider>
                <main className="septima-workspace min-w-0 flex-1 flex flex-col overflow-hidden">
                    <StaffPageToolbar frontend={frontend} />
                    <div className="flex-1 flex flex-col overflow-hidden">
                        {isWip ? (
                            <>
                                <WipBanner />
                                <div className="flex min-h-0 flex-1 flex-col">{children}</div>
                            </>
                        ) : (
                            children
                        )}
                    </div>
                </main>
            </PageToolbarProvider>
        </div>
    );
}
